/**
 * WHERE THE BOAT IS.
 *
 * The boat is never steered by hand. There is no held input, no heading the player sets,
 * no rudder, on any platform. The only things a player can do to the boat are:
 *
 *   - open the chart and choose a place already discovered      -> orderSailTo
 *   - press the one control that gives the boat back to the sea -> orderReturnToTrack
 *
 * The PERSISTED state is a single order. Everything a renderer or a simulation needs -
 * phase, position, water, heading, how far off the track it is - is DERIVED from that
 * order plus the current timestamp plus the simulation's progression. There is no
 * accumulator, no tick counter, and no per-frame integration anywhere in this file. That
 * is what makes an absent player's boat land exactly where a present player's boat would
 * land, including a player who left in the middle of a journey.
 *
 * -- EVERY REACHABLE STATE HAS AN EXIT ---------------------------------------
 *
 * Round 1 shipped a boat that could be parked and not un-parked from the surface that
 * parked it. The first tap a new save offers is the chart's only row, which is the
 * boat's own position; taking it moored the boat, and from then on the sea never changed
 * for that player, because ChartView carried no phase, no mooring and no exit.
 *
 * Two things changed. The exit is now on the chart itself - `chartView` takes the whole
 * fix and always returns a `HelmView` whose `exit` is present in every state, never null
 * (chart-view.ts). And the invariant is asserted over the REACHABLE STATE SPACE rather
 * than over the one path that found the defect: exits.test.ts enumerates the states
 * reachable from a fresh save under every control at every sampled time, and asserts of
 * each that the exit it offers actually returns the boat to its natural track.
 *
 * The mechanical statement, which `exitAvailability` makes true by construction:
 *
 *     for every reachable state s:
 *         s is the natural-drift state  XOR  the view at s offers a working exit
 *
 * -- WHAT THIS MODULE NEEDS FROM THE SIMULATION ------------------------------
 *
 *   interface ProgressSource { progressAt(ms): number; readonly curveId: string }
 *
 * `progressAt` is a pure function from wall-clock to a LIFETIME progression scalar that
 * is monotone non-decreasing and settled from timestamps rather than accumulated ticks.
 * sim-core is already required to be able to do exactly that by the offline invariant,
 * and sim-bridge.ts wires it.
 *
 * `curveId` is the half round 1 did not have. A world is solved against a progression
 * curve and remembers which one; a source declares which one it realises; and this file
 * refuses to resolve a fix when they disagree. That refusal is what makes it impossible
 * to repeat round 1's CL1-R1-03, where a world paced for one progression rate was driven
 * by a source paying 64 times more and every test in both lanes stayed green.
 *
 * Because the scalar never decreases, the high-water mark of progression IS its current
 * value, so discovery needs no history stored and no flag maintained. The cost of that
 * simplification is stated plainly: if sim-core's scalar ever decreased, places would
 * un-discover. The voyage layer cannot enforce that invariant; the bridge conformance
 * check in voyage.test.ts states it, and sim-core has to hold it.
 *
 * WHAT THIS MODULE GIVES BACK: position, water region and blend weights, heading, phase,
 * and the chart. It takes no entitlement, no purchase and no boost of any kind as an
 * input, and no-steering.test.ts holds that as a scan rather than a promise.
 */

import { VOYAGE_GEOMETRY } from './constants';
import { authoredCoastsFor, coastContains, nearestCoastWater, coastPathPoint, coastPathHeading, planCoastPath, segmentCrossesCoast, type Coast, type CoastPath } from './coast';
import { journeyDurationMs, journeyFraction, journeyPosition } from './journey';
import { trackDistanceAtProgress } from './pacing';
import type { NaturalFix, Standing } from './settled';
import { isSettled, naturalFixAt } from './settled';
import type { WaterRegionId } from './regions';
import { regionAt, regionWeightsAt } from './regions';
import type { Degrees, Leagues, Millis, Progress, Vec2 } from './units';
import { bearingDeg, distance, smoothstep, vec2 } from './units';
import type { VoyageWorld } from './world';
import { destinationById } from './destinations';

// ---------------------------------------------------------------------------
// The persisted order. Three shapes; four derived phases.
// ---------------------------------------------------------------------------

export interface DriftOrder {
  readonly kind: 'drift';
}

export interface SailOrder {
  readonly kind: 'sail';
  readonly to: string;
  readonly fromX: Leagues;
  readonly fromY: Leagues;
  readonly issuedAtMs: Millis;
  readonly durationMs: Millis;
}

export interface ReturnOrder {
  /** Predicted natural position at the published arrival instant; quarry choices are rate-neutral. */
  readonly coastJoin?: { readonly x: number; readonly y: number; readonly headingDeg: number };
  readonly kind: 'return';
  readonly fromX: Leagues;
  readonly fromY: Leagues;
  readonly issuedAtMs: Millis;
  readonly durationMs: Millis;
}

export type VoyageOrder = DriftOrder | SailOrder | ReturnOrder;

export const DRIFT_ORDER: DriftOrder = Object.freeze({ kind: 'drift' } as DriftOrder);

export type VoyagePhase = 'drifting' | 'sailing' | 'moored' | 'returning';

export interface JourneyFix {
  readonly destination: Vec2;
  /** null while returning to the track, which is not a place. */
  readonly destinationPlaceId: string | null;
  readonly elapsedMs: Millis;
  readonly durationMs: Millis;
  readonly fraction: number;
}

export interface VoyageFix {
  /** A legacy or edited order began inside a newly authored shoreline; progress is retained. */
  readonly courseRecovery?: 'shoreline-overlap' | 'shoreline-start-adjusted';
  readonly phase: VoyagePhase;
  readonly position: Vec2;
  /** Where the natural progression has the boat right now, journey or no journey. */
  readonly trackPosition: Vec2;
  readonly trackDistance: Leagues;
  readonly offTrackLeagues: Leagues;
  readonly region: WaterRegionId;
  readonly regionWeights: readonly number[];
  readonly headingDeg: Degrees;
  readonly journey: JourneyFix | null;
  readonly mooredAt: string | null;
  readonly progress: Progress;
  /** True once the whole charted sea has been sailed and the boat has taken up its round. */
  readonly beyondCharted: boolean;
  /**
   * WHAT THE BOAT IS DOING: `charted` inside the charted sea, then `lying`, `homeward` or
   * `seaward` on the round. It replaces round 3's `patrolLaps`, which counted laps of the
   * sea's empty rim and was the exact tell CONTRACT.md section 1.5d rules against. See
   * settled.ts.
   */
  readonly standing: Standing;
  /**
   * Leagues along the CHARTED track the boat is over. Inside the charted sea this is the
   * drift distance itself; on the round it is where on the outward track the boat has come
   * back to, and it never exceeds the charted end. Nothing recedes and nothing grows.
   */
  readonly overTrackLeagues: Leagues;
  /**
   * Hours into the current settled round. 0 inside the charted sea.
   *
   * Carried on the fix rather than re-solved by the chart view, so the readout and the
   * boat can never be answering about two different instants.
   */
  readonly roundPhaseHours: number;
}

export interface ProgressSource {
  progressAt(timestampMs: Millis): Progress;
  /**
   * HOURS OF PLAY THIS SAVE HAS BEEN PAID FOR, at that timestamp. The round's clock.
   *
   * -- WHY THIS EXISTS, AND IT IS NOT A CONVENIENCE ------------------------------
   *
   * `progressAt` returns XP, and XP IS CAPPED BY DESIGN at `MAX_XP` = 200,000,000 -
   * src/sim/step.ts pays it through `addCapped`, and src/sim/never-nerf.test.ts asserts a
   * save at the cap earns nothing further. That cap is correct and is not going anywhere.
   *
   * Round 5 first made the progression curve's inverse total, which fixed the freeze at
   * modelled hour 800 and did NOT fix the game: at MAX_XP the ARGUMENT to that inverse
   * stops moving, so the hours it returns stop moving, and the boat froze again - at
   * (0, 0), lying at home, from day 353 to for ever. Verified through
   * `openVoyageSession` at 3-hour resolution on day 950: seventeen consecutive readings
   * identical. No inverse, however total, can recover a varying output from a constant
   * input.
   *
   * SO THE SETTLED ROUND IS PACED BY THE SAVE'S OWN CLOCK INSTEAD. This quantity is
   * everything XP is - monotone, never spent, never reset, a persisted attribute rather
   * than a live input, derived from timestamps rather than counted frames, and identical
   * whether the tab was watched or closed - and it is bounded only by `CURSOR_CEILING`,
   * the tick index sim-core refuses to advance past. It is not elapsed wall-clock time:
   * time skipped past the 24-hour hold was never paid and the boat does not sail it, so
   * the hold's disclosure and the boat's position keep telling the same story.
   *
   * ROUND 5 SAID THAT AND SHIPPED ELAPSED TIME. The implementation added the current
   * absence's paid share to `state.tick`, and the cursor is elapsed time - it absorbs every
   * skipped span the moment it is skipped. Both sentences above were false from the first
   * skipped tick and the suite never ticked off the 24-hour cadence that hides it. The
   * derivation is now `tick - skippedTicks` plus the paid share of the current absence -
   * see `anchorPaidTicks` in sim-bridge.ts for why that subtraction has a stated domain
   * (`skippedTicks` saturates at MAX_COUNT, 31,688 years of skipped time) and what the
   * clock does outside it (refuses, publishing 0, rather than resuming elapsed time).
   *
   * BELOW THE XP CAP IT EQUALS `curve.hoursAtProgress(progressAt(t))` TO THE TICK, at
   * every cadence and after any absence, which is why nothing the lane had already
   * measured moved when the round changed clocks - paid-clock.test.ts asserts this at
   * cadences that are not multiples of the hold, and the sum of this and the hold's
   * skipped hours is the elapsed wall-clock, which is the two stories agreeing.
   */
  paidHoursAt(timestampMs: Millis): number;
  /**
   * Identity of the progression curve this source realises. It must equal the curve id
   * the world's pace was solved against, and `resolveVoyage` refuses when it does not.
   */
  readonly curveId: string;
}

/**
 * Below this the boat counts as on the track and a return order is a no-op. Leagues.
 *
 * DERIVED, not a literal. Round 2 held it as a bare 1e-3, which made it one of five
 * league-valued constants that did not move when the world scale did - so the advertised
 * single edit would have changed what "on the track" means by the whole scale factor.
 * It is a fixed fraction of a screen width; constants.ts owns the fraction.
 */
export const ON_TRACK_EPSILON_LEAGUES = VOYAGE_GEOMETRY.onTrackEpsilonLeagues;

/**
 * The check that makes a mispaced sea impossible to construct silently.
 *
 * It is a string comparison on the hot path, which is the cheapest instrument in this
 * lane and the one guarding its most expensive mistake. It throws rather than reporting,
 * because a curve mismatch is a wiring error that no save and no player input can cause:
 * it is wrong at the first frame or never, and a value returned from here would be a
 * position on a map that is quietly the wrong size.
 */
export function assertSourceMatchesWorld(world: VoyageWorld, sim: ProgressSource): void {
  if (sim.curveId !== world.pacing.curveId) {
    throw new Error(
      `voyage: this world was paced against progression curve '${world.pacing.curveId}' but is ` +
        `being driven by a source realising '${sim.curveId}'. The sea would be the wrong size. ` +
        `Build the world with the same curve the source realises (see sim-bridge.ts).`,
    );
  }
}

function safeProgress(sim: ProgressSource, nowMs: Millis): Progress {
  const raw = sim.progressAt(nowMs);
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
}

function safePaidHours(sim: ProgressSource, nowMs: Millis): number {
  const raw = sim.paidHoursAt(nowMs);
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
}

function elapsedOf(order: SailOrder | ReturnOrder, nowMs: Millis): Millis {
  const e = nowMs - order.issuedAtMs;
  return Number.isFinite(e) && e > 0 ? e : 0;
}

interface Natural {
  readonly fix: NaturalFix;
  /** The RAW monotone drift distance. Discovery is derived from this and nothing else. */
  readonly distance: Leagues;
  readonly beyond: boolean;
}

/**
 * The natural state, from progression alone.
 *
 * It reads the progression TWICE, in two units, and both are needed: the raw track
 * distance decides whether the charted sea is finished and is what discovery is measured
 * in, while the modelled hours pace the settled round. Reading hours costs the curve's
 * inverse; settled.ts's header states what that costs and why the round cannot be paced in
 * track distance instead.
 */
function naturalAt(world: VoyageWorld, progress: Progress, paidHours: number): Natural {
  const d = trackDistanceAtProgress(progress, world.pacing);
  const beyond = isSettled(world.sea, d);
  // THE CALLS ARE READ HERE AND NOWHERE ABOVE IT. `d` is the raw drift distance and is computed
  // before the route is consulted, so CONTRACT 1.5n's arrival state cannot reach discovery, the
  // cadence, the charted span or the pace. See calls.ts.
  // THE ROUND IS PACED BY THE SAVE'S CLOCK, NOT BY THE CURVE'S INVERSE. This line used to
  // read `modelledHoursAtProgress(progress, world.pacing)`, and that is where the game
  // died twice: once at the inverse's horizon and once at XP's own 200,000,000 cap, which
  // no inverse can see past because the cap is in its ARGUMENT. `paidHoursAt` is the same
  // number below the cap and keeps going above it. See ProgressSource.
  const hours = beyond ? paidHours : 0;
  return { fix: naturalFixAt(world.sea, d, hours, world.calls), distance: d, beyond };
}

function makeFix(
  phase: VoyagePhase,
  position: Vec2,
  natural: Natural,
  progress: Progress,
  heading: Degrees,
  journey: JourneyFix | null,
  mooredAt: string | null,
): VoyageFix {
  return {
    phase,
    position,
    trackPosition: natural.fix.position,
    trackDistance: natural.distance,
    offTrackLeagues: distance(position, natural.fix.position),
    region: regionAt(position),
    regionWeights: regionWeightsAt(position),
    headingDeg: heading,
    journey,
    mooredAt,
    progress,
    beyondCharted: natural.beyond,
    standing: natural.fix.standing,
    overTrackLeagues: natural.fix.overTrackLeagues,
    roundPhaseHours: natural.fix.roundPhaseHours,
  };
}

/** Plans are private derived caches, never mutable save authority or per-frame collision work. */
type PlannedCourse = { readonly path: CoastPath | null; readonly recover: boolean; readonly adjustedStart: boolean };
const coastWorlds = new WeakMap<VoyageWorld, { coasts: readonly Coast[]; orders: WeakMap<VoyageOrder, PlannedCourse> }>();
function coastWorld(world: VoyageWorld) {
  let cache = coastWorlds.get(world);
  if (cache === undefined) { cache = { coasts: authoredCoastsFor(world.chart.places), orders: new WeakMap() }; coastWorlds.set(world, cache); }
  return cache;
}
function plannedCourse(world: VoyageWorld, order: SailOrder | ReturnOrder, destination: Vec2, sim: ProgressSource): PlannedCourse {
  const cache = coastWorld(world);
  const found = cache.orders.get(order); if (found !== undefined) return found;
  const original = vec2(order.fromX, order.fromY);
  const from = nearestCoastWater(original, cache.coasts);
  const adjustedStart = from !== original;
  let to = destination; let endHeading: number | undefined;
  if (order.kind === 'return' && cache.coasts.length > 0) {
    const end = order.issuedAtMs + order.durationMs;
    const projected = naturalAt(world, safeProgress(sim, end), safePaidHours(sim, end)).fix;
    // Validate the persisted hint against this save's projection once. Old builds have no hint;
    // edited hints cannot send a valid boat somewhere other than its natural terminal position.
    const saved = order.coastJoin;
    to = saved !== undefined && distance(saved, projected.position) < 1e-7 ? vec2(saved.x, saved.y) : projected.position;
    endHeading = projected.headingDeg;
  }
  const recover = cache.coasts.some(coast => coastContains(coast, to));
  const blocked = !recover && cache.coasts.some(coast => segmentCrossesCoast(from, to, coast));
  const path = !recover && cache.coasts.length > 0 && (order.kind === 'return' || blocked || adjustedStart)
    ? planCoastPath(from, to, cache.coasts, undefined, endHeading) : null;
  const result = { path, recover, adjustedStart }; cache.orders.set(order, result); return result;
}

/**
 * The whole state of the voyage, derived. Pure: same arguments, same result, forever,
 * and it never touches the order it is given.
 */
export function resolveVoyage(
  world: VoyageWorld,
  order: VoyageOrder,
  nowMs: Millis,
  sim: ProgressSource,
): VoyageFix {
  assertSourceMatchesWorld(world, sim);

  const progress = safeProgress(sim, nowMs);
  const natural = naturalAt(world, progress, safePaidHours(sim, nowMs));

  if (order.kind === 'sail') {
    const place = world.chart.byId.get(order.to) ?? destinationById(world, order.to);
    if (place !== undefined) {
      const from = vec2(order.fromX, order.fromY);
      const elapsed = elapsedOf(order, nowMs);
      const fraction = journeyFraction(elapsed, order.durationMs);
      const course = plannedCourse(world, order, place.position, sim);
      if (course.recover) return { ...makeFix('drifting', natural.fix.position, natural, progress, natural.fix.headingDeg, null, natural.fix.callingAt), courseRecovery: 'shoreline-overlap' };
      const heading = course.path === null ? bearingDeg(from, place.position) : coastPathHeading(course.path, smoothstep(fraction));
      if (fraction >= 1) {
        return makeFix('moored', place.position, natural, progress, heading, null, place.id);
      }
      return { ...makeFix(
        'sailing',
        course.path === null ? journeyPosition(from, place.position, elapsed, order.durationMs) : coastPathPoint(course.path, smoothstep(fraction)),
        natural,
        progress,
        heading,
        {
          destination: place.position,
          destinationPlaceId: place.id,
          elapsedMs: elapsed,
          durationMs: order.durationMs,
          fraction,
        },
        null,
      ), ...(course.adjustedStart ? { courseRecovery: 'shoreline-start-adjusted' as const } : {}) };
    }
    // Unknown place id: a save written against a different place table. The boat goes
    // back to the sea rather than to a coordinate nobody can name.
  } else if (order.kind === 'return') {
    const from = vec2(order.fromX, order.fromY);
    const elapsed = elapsedOf(order, nowMs);
    const fraction = journeyFraction(elapsed, order.durationMs);
    if (fraction < 1) {
      const course = plannedCourse(world, order, natural.fix.position, sim);
      if (course.recover) return { ...makeFix('drifting', natural.fix.position, natural, progress, natural.fix.headingDeg, null, natural.fix.callingAt), courseRecovery: 'shoreline-overlap' };
      // Coast-bearing worlds plan to the predicted terminal point once. Older/no-coast worlds
      // retain their live homing interpolation. Both meet the natural course at the exact ETA.
      // The target moves while the journey runs, because the track does not wait.
      // Homing onto the LIVE track position means arrival is exact and there is never a
      // snap at the end, and it is still closed form in `now`.
      return { ...makeFix(
        'returning',
        course.path === null ? journeyPosition(from, natural.fix.position, elapsed, order.durationMs) : coastPathPoint(course.path, smoothstep(fraction)),
        natural,
        progress,
        course.path === null ? bearingDeg(from, natural.fix.position) : coastPathHeading(course.path, smoothstep(fraction)),
        {
          destination: natural.fix.position,
          destinationPlaceId: null,
          elapsedMs: elapsed,
          durationMs: order.durationMs,
          fraction,
        },
        null,
      ), ...(course.adjustedStart ? { courseRecovery: 'shoreline-start-adjusted' as const } : {}) };
    }
  }

  // THE DRIFTING BOAT CAN BE SOMEWHERE. CONTRACT 1.5n(a): when the natural route has the boat made
  // fast at a charted place, `mooredAt` names it, and the chart's own copy reads that as "made
  // fast" with no edit to the copy layer. The PHASE stays `drifting` on purpose: a call is the
  // boat living its own progression, not a deviation from it, so `exitAvailability` correctly
  // offers no exit and exits.test.ts's reachable-state invariant is untouched.
  return makeFix('drifting', natural.fix.position, natural, progress, natural.fix.headingDeg, null, natural.fix.callingAt);
}

// ---------------------------------------------------------------------------
// Discovery, and the two controls.
// ---------------------------------------------------------------------------

/** Discovered iff the natural drift has already carried the boat within sight of it. */
export function isDiscovered(world: VoyageWorld, placeId: string, progress: Progress): boolean {
  if (destinationById(world, placeId) !== undefined) return true;
  const place = world.chart.byId.get(placeId);
  if (place === undefined) return false;
  return trackDistanceAtProgress(progress, world.pacing) >= place.sightTrackDistance;
}

export type SailRefusalReason = 'unknown-place' | 'not-discovered';

export type SailResult =
  | { readonly ok: true; readonly order: SailOrder }
  | { readonly ok: false; readonly reason: SailRefusalReason };

/**
 * The chart's one verb. Refuses rather than throws, so a stale UI or a mangled save can
 * never brick the boat.
 *
 * It accepts the place the boat is already at, which round 1's critic flagged as a
 * degenerate selection - and it still does, deliberately. W2 promises that ANY
 * discovered place is one selection away from ANY state, and carving an exception out of
 * that promise trades a clear rule for a special case. What made the degenerate
 * selection dangerous was not the zero-length journey; it was that mooring had no exit
 * on the surface that offered it. The exit is what changed. `chartView` marks the row the
 * boat occupies with `isHere` so a surface can present it as a position rather than a
 * destination.
 */
export function orderSailTo(
  world: VoyageWorld,
  order: VoyageOrder,
  nowMs: Millis,
  sim: ProgressSource,
  placeId: string,
): SailResult {
  const place = world.chart.byId.get(placeId) ?? destinationById(world, placeId);
  if (place === undefined) return { ok: false, reason: 'unknown-place' };

  const here = resolveVoyage(world, order, nowMs, sim);
  if (!isDiscovered(world, placeId, here.progress)) return { ok: false, reason: 'not-discovered' };

  return {
    ok: true,
    order: {
      kind: 'sail',
      to: place.id,
      fromX: here.position.x,
      fromY: here.position.y,
      issuedAtMs: nowMs,
      durationMs: journeyDurationMs(distance(here.position, place.position), world.journeyLaw),
    },
  };
}

/** The one control that gives the boat back to its natural progression. */
export function orderReturnToTrack(
  world: VoyageWorld,
  order: VoyageOrder,
  nowMs: Millis,
  sim: ProgressSource,
): VoyageOrder {
  const here = resolveVoyage(world, order, nowMs, sim);
  if (here.offTrackLeagues <= ON_TRACK_EPSILON_LEAGUES) return DRIFT_ORDER;
  const durationMs = journeyDurationMs(here.offTrackLeagues, world.journeyLaw);
  const end = nowMs + durationMs;
  const terminal = naturalAt(world, safeProgress(sim, end), safePaidHours(sim, end)).fix;
  return {
    kind: 'return',
    fromX: here.position.x,
    fromY: here.position.y,
    issuedAtMs: nowMs,
    durationMs,
    ...(coastWorld(world).coasts.length === 0 ? {} : { coastJoin: { x: terminal.position.x, y: terminal.position.y, headingDeg: terminal.headingDeg } }),
  };
}

/**
 * THE EXIT, as data rather than as a promise.
 *
 * `needed` is false in exactly one situation: the boat is already living its natural
 * progression - drifting, and on the track. In every other state it is true and
 * `etaSeconds` says how long pressing the control takes. There is no fifth case and no
 * null, so a surface that renders a `HelmView` cannot render a state with no way out.
 */
export interface ExitAvailability {
  /** True unless the boat is already drifting on its natural track. */
  readonly needed: boolean;
  /**
   * EXACT duration of the return, in milliseconds, equal to the `durationMs` that
   * `orderReturnToTrack` would issue. A caller scheduling anything must use this and not
   * `etaSeconds`: seconds are the display figure, and `ms / 1000 * 1000` is not `ms` in
   * binary floating point, which is enough to leave the boat one millisecond short of
   * arriving. exits.test.ts caught exactly that.
   */
  readonly etaMs: Millis;
  /** Seconds the return takes, for display. 0 when nothing needs returning. */
  readonly etaSeconds: number;
  /** Leagues the boat has to come back. 0 when nothing needs returning. */
  readonly offTrackLeagues: Leagues;
}

export function exitAvailability(world: VoyageWorld, fix: VoyageFix): ExitAvailability {
  const onTrack = fix.offTrackLeagues <= ON_TRACK_EPSILON_LEAGUES;
  if (fix.phase === 'drifting' && onTrack) {
    return { needed: false, etaMs: 0, etaSeconds: 0, offTrackLeagues: 0 };
  }
  // Zero when the boat is already at the track's coordinate but not living it - the
  // first-run case, where the only row on the chart is the boat's own position. The
  // figure agrees with `orderReturnToTrack` by construction, and exits.test.ts asserts
  // the agreement over the reachable state space rather than trusting it.
  const etaMs = onTrack ? 0 : journeyDurationMs(fix.offTrackLeagues, world.journeyLaw);
  return {
    needed: true,
    etaMs,
    etaSeconds: etaMs / 1000,
    offTrackLeagues: fix.offTrackLeagues,
  };
}

// ---------------------------------------------------------------------------
// Persistence. One order, one version, and a parse that cannot throw.
// ---------------------------------------------------------------------------

export const VOYAGE_ORDER_SCHEMA_VERSION = 1;

export function serializeOrder(order: VoyageOrder): string {
  return JSON.stringify({ v: VOYAGE_ORDER_SCHEMA_VERSION, ...order });
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Never throws and never returns something the resolver cannot handle. Anything it does
 * not recognise becomes a drifting boat, which is the state a new save starts in.
 */
export function parseOrder(raw: unknown): VoyageOrder {
  let value: unknown = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return DRIFT_ORDER;
    }
  }
  if (typeof value !== 'object' || value === null) return DRIFT_ORDER;

  const record = value as Record<string, unknown>;
  if (record['v'] !== VOYAGE_ORDER_SCHEMA_VERSION) return DRIFT_ORDER;

  const fromX = finiteNumber(record['fromX']);
  const fromY = finiteNumber(record['fromY']);
  const issuedAtMs = finiteNumber(record['issuedAtMs']);
  const durationMs = finiteNumber(record['durationMs']);
  const complete = fromX !== null && fromY !== null && issuedAtMs !== null && durationMs !== null && durationMs > 0;

  if (record['kind'] === 'sail') {
    const to = record['to'];
    if (typeof to !== 'string' || !complete) return DRIFT_ORDER;
    return { kind: 'sail', to, fromX, fromY, issuedAtMs, durationMs };
  }
  if (record['kind'] === 'return') {
    if (!complete) return DRIFT_ORDER;
    let coastJoin: ReturnOrder['coastJoin'];
    try {
      const rawJoin = record['coastJoin'];
      if (typeof rawJoin === 'object' && rawJoin !== null) {
        const join = rawJoin as Record<string, unknown>;
        const x = finiteNumber(join['x']), y = finiteNumber(join['y']), headingDeg = finiteNumber(join['headingDeg']);
        if (x !== null && y !== null && headingDeg !== null && Math.abs(x) <= 1_000_000 && Math.abs(y) <= 1_000_000) coastJoin = { x, y, headingDeg };
      }
    } catch { /* A malformed optional hint never invalidates the player's existing order. */ }
    return { kind: 'return', fromX, fromY, issuedAtMs, durationMs, ...(coastJoin === undefined ? {} : { coastJoin }) };
  }
  return DRIFT_ORDER;
}
